/**
 * Session-scoped in-memory cache for Google Maps Geocoding / Street View metadata.
 *
 * Google's ToS restrict long-term storage of Geocoding results — keep TTLs short
 * (session lifetime) and do not persist to disk or localStorage.
 */

export type CacheStats = {
  size: number;
  hits: number;
  misses: number;
  coalesced: number;
};

type Entry<T> = {
  value: T;
  expiresAt: number;
};

/** Default TTL: one browser session is ~30 min of active use; refresh after that. */
export const GOOGLE_MAPS_CACHE_TTL_MS = 30 * 60 * 1000;
export const GOOGLE_MAPS_CACHE_MAX_ENTRIES = 200;

export class SessionTtlLruCache<T> {
  private readonly store = new Map<string, Entry<T>>();
  private readonly inflight = new Map<string, Promise<T>>();
  private hits = 0;
  private misses = 0;
  private coalesced = 0;

  constructor(
    private readonly maxEntries = GOOGLE_MAPS_CACHE_MAX_ENTRIES,
    private readonly ttlMs = GOOGLE_MAPS_CACHE_TTL_MS,
    private readonly now: () => number = () => Date.now(),
  ) {}

  get(key: string): T | undefined {
    const entry = this.store.get(key);
    if (!entry) {
      this.misses += 1;
      return undefined;
    }
    if (entry.expiresAt <= this.now()) {
      this.store.delete(key);
      this.misses += 1;
      return undefined;
    }
    // LRU: re-insert to move to end
    this.store.delete(key);
    this.store.set(key, entry);
    this.hits += 1;
    return entry.value;
  }

  set(key: string, value: T): void {
    if (this.store.has(key)) this.store.delete(key);
    this.store.set(key, { value, expiresAt: this.now() + this.ttlMs });
    this.evictOverflow();
  }

  has(key: string): boolean {
    return this.get(key) !== undefined;
  }

  /**
   * Coalesce concurrent loaders for the same key into a single promise.
   * Failed loads are not cached so a later retry can succeed.
   */
  async getOrLoad(key: string, loader: () => Promise<T>): Promise<T> {
    const cached = this.get(key);
    if (cached !== undefined) return cached;

    const pending = this.inflight.get(key);
    if (pending) {
      this.coalesced += 1;
      return pending;
    }

    const promise = (async () => {
      try {
        const value = await loader();
        this.set(key, value);
        return value;
      } finally {
        this.inflight.delete(key);
      }
    })();

    this.inflight.set(key, promise);
    return promise;
  }

  clear(): void {
    this.store.clear();
    this.inflight.clear();
  }

  stats(): CacheStats {
    return {
      size: this.store.size,
      hits: this.hits,
      misses: this.misses,
      coalesced: this.coalesced,
    };
  }

  private evictOverflow(): void {
    while (this.store.size > this.maxEntries) {
      const oldest = this.store.keys().next().value;
      if (oldest === undefined) break;
      this.store.delete(oldest);
    }
  }
}

export function normalizeGeocodeAddressKey(address: string): string {
  return address.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function geocodeCacheKey(input: {
  address: string;
  country?: string | null;
  biasLat?: number | null;
  biasLng?: number | null;
}): string {
  const addr = normalizeGeocodeAddressKey(input.address);
  const country = (input.country ?? '').trim().toUpperCase() || '-';
  const bias =
    input.biasLat != null &&
    input.biasLng != null &&
    Number.isFinite(input.biasLat) &&
    Number.isFinite(input.biasLng)
      ? `${input.biasLat.toFixed(3)},${input.biasLng.toFixed(3)}`
      : '-';
  return `geocode:${country}:${bias}:${addr}`;
}

/** Round building coords so nearby probes share a cache bucket (~11 m at equator). */
export function streetViewRingCacheKey(input: {
  lat: number;
  lng: number;
  ringOffsetM: number;
  ringRadiusM: number;
}): string {
  return `sv:${input.lat.toFixed(4)},${input.lng.toFixed(4)}:o${input.ringOffsetM}:r${input.ringRadiusM}`;
}

/**
 * Pick which addresses to spend the geocode budget on.
 *
 * The budget exists to cap per-render Google calls, but dropping the wrong
 * address is not a cosmetic loss: without the employer address there is no
 * mismatch detection and possibly no employer pin at all. Anything in
 * `required` therefore claims a slot before `optional` (purchase streets) is
 * considered, regardless of how many purchases lack coordinates.
 */
export function buildGeocodeQueue(input: {
  required: (string | null | undefined)[];
  optional: (string | null | undefined)[];
  limit: number;
}): string[] {
  const queue: string[] = [];
  const seen = new Set<string>();

  const push = (raw: string | null | undefined) => {
    if (queue.length >= input.limit) return;
    const trimmed = (raw ?? '').trim();
    if (!trimmed) return;
    const key = normalizeGeocodeAddressKey(trimmed);
    if (seen.has(key)) return;
    seen.add(key);
    queue.push(trimmed);
  };

  for (const address of input.required) push(address);
  for (const address of input.optional) push(address);
  return queue;
}

/** Shared session caches — one geocode map, one Street View metadata map. */
export const geocodeSessionCache = new SessionTtlLruCache<unknown>();
export const streetViewSessionCache = new SessionTtlLruCache<unknown>();
